import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Archive, RefreshCw, FileText, Upload,
  Download, Trash2, Loader2, Save, Pencil, X, EyeOff, Eye, Clock,
  History, AlertTriangle, Calculator, Globe, Shield, Flag, Building2,
  Tag, Coins, Info,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATE_FIELDS = [
  { key: "discount_rate_pct",       label: "Discount rate",   suffix: "%", kind: "pct" },
  { key: "settlement_fee_pct",      label: "Settlement fee",  suffix: "%", kind: "pct" },
  { key: "transaction_fee_amount",  label: "Per-txn fee",     suffix: "",  kind: "amount" },
  { key: "monthly_fee_amount",      label: "Monthly fee",     suffix: "",  kind: "amount" },
  { key: "setup_fee_amount",        label: "Setup fee",       suffix: "",  kind: "amount" },
  { key: "chargeback_fee_amount",   label: "Chargeback fee",  suffix: "",  kind: "amount" },
  { key: "rolling_reserve_pct",     label: "Rolling reserve", suffix: "%", kind: "pct" },
  { key: "rolling_reserve_days",    label: "Reserve days",    suffix: "d", kind: "days" },
];

const DOC_KIND_LABELS = {
  contract: "Contract",
  rate_sheet: "Rate sheet",
  addendum: "Addendum",
};

const PRODUCT_LABELS = {
  ACQUIRING: "Acquiring", ONRAMP: "Onramp", CRYPTO: "Crypto",
  PAYOUT: "Payout", OTHER: "Other",
};
const METHOD_LABELS = {
  CARDS: "Cards", APM: "APM", CRYPTO: "Crypto", BANK: "Bank", OTHER: "Other",
};
const REGION_LABELS = {
  EU: "EU", UK: "UK", US: "US", LATAM: "LATAM", APAC: "APAC",
  ROW: "ROW", WW: "Global",
};
const CAPABILITY_LABELS = {
  FTD_capable: "FTD capable",
  trusted_capable: "Trusted-merchant capable",
  "3DS_enforced": "3DS enforced",
  MOTO: "MOTO",
  recurring: "Recurring",
  refunds: "Refunds",
  chargebacks: "Chargebacks",
};
const THREE_DS_LABELS = {
  enforced: "3DS enforced",
  optional: "3DS optional",
  not_supported: "3DS not supported",
  unknown: "3DS posture unknown",
};

function fmtRate(field, value) {
  if (value === null || value === undefined) {
    // Sell settlement fee may legitimately be null (flat-fee provider,
    // e.g. Madfin 60 EUR + FX). Render an explicit "flat" pill rather
    // than "—" so AMs know it's intentional, not missing.
    return field.key === "settlement_fee_pct" ? "flat" : "—";
  }
  if (field.kind === "days") return `${value}d`;
  if (field.kind === "pct") return `${Number(value).toFixed(3)}%`;
  return Number(value).toFixed(2);
}

function MarginAlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  const human = {
    low_discount_rate_margin: "Discount-rate margin below 0.5pp",
    low_settlement_fee_margin: "Settlement-fee margin below 0.5pp",
    low_per_txn_margin: "Per-txn margin below 0.10 (settlement ccy)",
  };
  return (
    <div
      data-testid="margin-alerts-banner"
      className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-4 mb-5 flex items-start gap-3"
    >
      <AlertTriangle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[12.5px] text-amber-200 mb-1 font-medium">Margin alerts</div>
        <ul className="text-[11.5px] text-amber-100/80 space-y-0.5">
          {alerts.map((a) => (
            <li key={a} data-testid={`margin-alert-${a}`}>
              · {human[a] || a.replace("negative_margin:", "Negative margin on ")}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function UnifiedRatesTable({ buy, sell, margin, revealed, currency, onReveal }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="unified-rates-table">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">— Buy · sell · margin</div>
        {!revealed && (
          <button
            type="button"
            onClick={onReveal}
            data-testid="unified-reveal-btn"
            className="inline-flex items-center gap-1 text-[10.5px] text-amber-300 hover:text-amber-200 rounded-md border border-amber-400/30 bg-amber-400/[0.08] px-2 py-0.5"
          >
            <Eye className="h-3 w-3" /> Reveal buy + margin
          </button>
        )}
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            <th className="text-left py-2 font-normal">Field</th>
            <th className="text-right py-2 px-2 font-normal text-amber-300/80">Buy</th>
            <th className="text-right py-2 px-2 font-normal text-emerald-300/80">Sell</th>
            <th className="text-right py-2 px-2 font-normal text-cyan-300/80">Margin</th>
          </tr>
        </thead>
        <tbody>
          {RATE_FIELDS.map((f) => {
            const sellVal = sell?.[f.key];
            const buyVal = revealed ? buy?.[f.key] : null;
            const marginVal = revealed && margin ? margin[f.key] : null;
            const isThin = revealed && margin && (
              (f.key === "discount_rate_pct" && marginVal != null && marginVal < 0.5) ||
              (f.key === "settlement_fee_pct" && marginVal != null && marginVal < 0.5) ||
              (f.key === "transaction_fee_amount" && marginVal != null && marginVal < 0.10) ||
              (marginVal != null && marginVal < 0)
            );
            return (
              <tr key={f.key} className="border-b border-white/[0.04] last:border-0">
                <td className="py-2 text-[12px] text-zinc-400">{f.label}</td>
                <td className="py-2 px-2 text-right text-[12px] font-mono tabular-nums text-amber-200" data-testid={`unified-buy-${f.key}`}>
                  {revealed ? fmtRate(f, buyVal) : <span className="text-zinc-600 select-none">••••</span>}
                </td>
                <td className="py-2 px-2 text-right text-[12px] font-mono tabular-nums text-white" data-testid={`unified-sell-${f.key}`}>
                  {fmtRate(f, sellVal)}
                </td>
                <td
                  className={`py-2 px-2 text-right text-[12px] font-mono tabular-nums ${isThin ? "text-amber-300" : "text-cyan-200"}`}
                  data-testid={`unified-margin-${f.key}`}
                >
                  {revealed ? (
                    marginVal === null || marginVal === undefined ? "—" :
                    f.kind === "days" ? `${marginVal}d` :
                    f.kind === "pct" ? `${marginVal.toFixed(3)}pp` :
                    marginVal.toFixed(2)
                  ) : <span className="text-zinc-600 select-none">••••</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 text-[10.5px] text-zinc-600">
        Settlement currency: <span className="text-zinc-400 font-mono">{currency}</span>
        {revealed && <span className="ml-3 text-emerald-400/80"><Clock className="h-3 w-3 inline mr-0.5" /> Buy access revealed for this session</span>}
      </div>
    </div>
  );
}

function AtAGlanceSellCard({ sell, currency }) {
  if (!sell) return null;
  const settleFee = sell.settlement_fee_pct;
  return (
    <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.03] p-5" data-testid="at-a-glance-sell-card">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-emerald-300 mb-3">— At a glance · sell</div>
      <div className="grid grid-cols-5 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">MDR</div>
          <div className="text-[22px] font-light text-white tabular-nums" data-testid="glance-sell-mdr">
            {sell.discount_rate_pct != null ? `${Number(sell.discount_rate_pct).toFixed(2)}%` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Settlement fee</div>
          <div className="text-[22px] font-light text-white tabular-nums" data-testid="glance-sell-settlement-fee">
            {settleFee == null
              ? <span className="text-zinc-500">flat</span>
              : `${Number(settleFee).toFixed(2)}%`}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Per-txn</div>
          <div className="text-[22px] font-light text-white tabular-nums" data-testid="glance-sell-pertxn">
            {sell.transaction_fee_amount != null ? Number(sell.transaction_fee_amount).toFixed(2) : "—"}
            <span className="text-[11px] text-zinc-500 ml-1.5 font-mono">{currency}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Chargeback</div>
          <div className="text-[22px] font-light text-white tabular-nums" data-testid="glance-sell-cb">
            {sell.chargeback_fee_amount != null ? Number(sell.chargeback_fee_amount).toFixed(0) : "—"}
            <span className="text-[11px] text-zinc-500 ml-1.5 font-mono">{currency}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Rolling reserve</div>
          <div className="text-[22px] font-light text-white tabular-nums" data-testid="glance-sell-reserve">
            {sell.rolling_reserve_pct != null ? `${Number(sell.rolling_reserve_pct).toFixed(0)}%` : "—"}
            <span className="text-[11px] text-zinc-500 ml-1.5">· {sell.rolling_reserve_days ?? 0}d</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function MethodCoverageCard({ sol }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="method-coverage-card">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1.5">
        <Globe className="h-3 w-3" /> — Method · coverage
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Product</div>
          <div className="text-[13px] text-white" data-testid="coverage-product">
            {PRODUCT_LABELS[sol.product_type] || sol.product_type || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Method</div>
          <div className="text-[13px] text-white" data-testid="coverage-method">
            {METHOD_LABELS[sol.method] || sol.method || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Region</div>
          <div className="text-[13px] text-white" data-testid="coverage-region">
            {REGION_LABELS[sol.region] || sol.region || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">3DS posture</div>
          <div className="text-[13px] text-white inline-flex items-center gap-1" data-testid="coverage-three-ds">
            <Shield className="h-3 w-3 text-zinc-500" />
            {THREE_DS_LABELS[sol.three_ds_posture] || sol.three_ds_posture || "unknown"}
          </div>
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Countries</div>
          <div className="text-[12px] text-zinc-300 font-mono tabular-nums" data-testid="coverage-countries">
            {sol.countries && sol.countries.length > 0 ? sol.countries.join(", ") : <span className="text-zinc-600">—</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderStrip({ sol }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="provider-strip">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1.5">
        <Building2 className="h-3 w-3" /> — Provider
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Provider</div>
          <div className="text-[13px] text-white" data-testid="provider-name">{sol.provider || <span className="text-zinc-600">—</span>}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Internal code</div>
          <div className="text-[12.5px] text-zinc-300 font-mono" data-testid="provider-internal-code">{sol.internal_code || <span className="text-zinc-600">—</span>}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Acquiring bank</div>
          <div className="text-[12.5px] text-white" data-testid="provider-acq-bank">
            {sol.acquiring_bank || <span className="text-zinc-600">—</span>}
            {sol.acquiring_bank_country && <span className="text-zinc-500 ml-1.5 font-mono">· {sol.acquiring_bank_country}</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Introducer</div>
          <div className="text-[12.5px] text-zinc-300" data-testid="provider-introducer">{sol.introducer || <span className="text-zinc-600">—</span>}</div>
        </div>
      </div>
    </div>
  );
}

function CapabilityFlags({ flags }) {
  if (!flags || flags.length === 0) {
    return (
      <div className="text-[11.5px] text-zinc-600 italic" data-testid="capability-flags-empty">No capability flags parsed.</div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="capability-flags">
      {flags.map((f) => (
        <span
          key={f}
          data-testid={`capability-flag-${f}`}
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-200"
        >
          <Flag className="h-2.5 w-2.5" /> {CAPABILITY_LABELS[f] || f}
        </span>
      ))}
    </div>
  );
}

function OpsNotesCard({ sol }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="ops-notes-card">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1.5">
        <Coins className="h-3 w-3" /> — Ops · commercial
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Settlement T+</div>
          <div className="text-[13px] text-white tabular-nums" data-testid="ops-settlement-days">
            {sol.settlement_terms_days ?? "—"}d
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Settlement fee</div>
          <div className="text-[13px] text-white tabular-nums" data-testid="ops-settlement-fee">
            {sol.settlement_fee_pct != null ? `${Number(sol.settlement_fee_pct).toFixed(2)}%` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Refund fee</div>
          <div className="text-[13px] text-white tabular-nums" data-testid="ops-refund-fee">
            {sol.refund_fee_amount != null ? `${Number(sol.refund_fee_amount).toFixed(2)} ${sol.settlement_currency || ""}` : "—"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { key: "monthly_volume_max", label: "Monthly vol max" },
          { key: "monthly_txn_count_max", label: "Monthly txn max" },
          { key: "daily_volume_max", label: "Daily vol max" },
          { key: "max_ticket_amount", label: "Max ticket" },
        ].map((v) => (
          <div key={v.key}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">{v.label}</div>
            <div className="text-[12px] text-zinc-300 font-mono" data-testid={`ops-velocity-${v.key}`}>
              {sol.velocity_limits?.[v.key] ?? "—"}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Notes</div>
        <div className="text-[12px] text-zinc-300 whitespace-pre-wrap" data-testid="ops-notes-text">
          {sol.notes || <span className="text-zinc-600">No notes</span>}
        </div>
      </div>
    </div>
  );
}

// Addition A — Quick-quote builder.
function QuickQuoteCard({ sol }) {
  const [volume, setVolume] = useState(100000);
  const [avgTicket, setAvgTicket] = useState(75);

  const result = useMemo(() => {
    const sell = sol.sell_rates || {};
    const v = Number(volume) || 0;
    const t = Number(avgTicket) || 0;
    if (v <= 0 || t <= 0) return null;
    const txns = v / t;
    const mdrCost = v * (Number(sell.discount_rate_pct || 0) / 100);
    const perTxnCost = txns * Number(sell.transaction_fee_amount || 0);
    const monthlyCost = Number(sell.monthly_fee_amount || 0);
    // Settlement fee priority: sell_rates.settlement_fee_pct (new
    // per-side field) → top-level settlement_fee_pct (back-compat).
    // null/undefined sell-side means "flat fee, no % uplift" (e.g.
    // Madfin 60 EUR + 0.2% FX) — show as 0 in the quote.
    const settleFeePct =
      sell.settlement_fee_pct != null
        ? Number(sell.settlement_fee_pct)
        : Number(sol.settlement_fee_pct ?? 0);
    const settlementFeeCost = v * (settleFeePct / 100);
    const isFlat = sell.settlement_fee_pct == null;
    const total = mdrCost + perTxnCost + monthlyCost + settlementFeeCost;
    return {
      txns: Math.round(txns),
      mdrCost, perTxnCost, monthlyCost, settlementFeeCost, total, isFlat,
      effectiveRate: v > 0 ? (total / v) * 100 : 0,
    };
  }, [volume, avgTicket, sol]);

  const ccy = sol.settlement_currency || "";
  const fmt = (n) => n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—";

  return (
    <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.03] p-5" data-testid="quick-quote-card">
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-cyan-300 mb-3 flex items-center gap-1.5">
        <Calculator className="h-3 w-3" /> — Quick quote
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Monthly volume ({ccy})</label>
          <input
            type="number" min="0" step="1000"
            value={volume}
            onChange={(e) => setVolume(e.target.value)}
            data-testid="quick-quote-volume-input"
            className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[13px] text-white font-mono focus:outline-none focus:border-cyan-400/40"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Avg ticket ({ccy})</label>
          <input
            type="number" min="0" step="1"
            value={avgTicket}
            onChange={(e) => setAvgTicket(e.target.value)}
            data-testid="quick-quote-ticket-input"
            className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[13px] text-white font-mono focus:outline-none focus:border-cyan-400/40"
          />
        </div>
      </div>
      {result ? (
        <div className="space-y-1.5" data-testid="quick-quote-result">
          <div className="flex justify-between text-[12px] text-zinc-300">
            <span>Est. transactions</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-txns">{result.txns.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-[12px] text-zinc-300">
            <span>MDR cost</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-mdr-cost">{fmt(result.mdrCost)} {ccy}</span>
          </div>
          <div className="flex justify-between text-[12px] text-zinc-300">
            <span>Per-txn fees</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-pertxn-cost">{fmt(result.perTxnCost)} {ccy}</span>
          </div>
          <div className="flex justify-between text-[12px] text-zinc-300">
            <span>Settlement fee {result.isFlat && <span className="text-zinc-600 text-[10.5px]">(flat — negotiate)</span>}</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-settle-cost">{fmt(result.settlementFeeCost)} {ccy}</span>
          </div>
          <div className="flex justify-between text-[12px] text-zinc-300">
            <span>Monthly fee</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-monthly-cost">{fmt(result.monthlyCost)} {ccy}</span>
          </div>
          <div className="h-px bg-cyan-400/20 my-2"></div>
          <div className="flex justify-between text-[13px] text-white">
            <span className="font-medium">Total est. monthly cost</span>
            <span className="font-mono tabular-nums text-cyan-200" data-testid="quick-quote-total">{fmt(result.total)} {ccy}</span>
          </div>
          <div className="flex justify-between text-[11.5px] text-zinc-500">
            <span>Effective rate</span>
            <span className="font-mono tabular-nums" data-testid="quick-quote-effective-rate">{result.effectiveRate.toFixed(2)}%</span>
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-zinc-600">Enter values to estimate monthly cost.</div>
      )}
    </div>
  );
}

function RevealModal({ open, onClose, solutionId, onRevealed }) {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/admin/solutions/${solutionId}/reveal-buy-rates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, reason: reason.trim() || null }),
      });
      const data = await r.json();
      if (!r.ok) {
        if (r.status === 423) throw new Error(data?.detail || "Locked");
        if (r.status === 412) throw new Error("No reveal password set — ask super_admin via /admin/team.");
        throw new Error(data?.detail || `${r.status}`);
      }
      toast.success("Buy rates revealed for 15 minutes");
      setPassword(""); setReason("");
      onRevealed(data);
      onClose();
    } catch (err) {
      toast.error(String(err.message || "Reveal failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} data-testid="reveal-modal"
        className="w-full max-w-md mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6">
        <div className="flex items-center gap-2 mb-1">
          <Eye className="h-4 w-4 text-amber-400" />
          <h2 className="text-[14px] font-medium text-white">Reveal buy rates</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-1">
          Enter your buy-rate reveal password. Access lasts 15 minutes on this session only.
        </p>
        <p className="text-[10.5px] text-zinc-600 mb-5">
          5 failed attempts in 10 minutes locks your reveal access for 1 hour.
        </p>
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Reveal password</label>
        <input
          type="password" required value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="reveal-password-input"
          autoFocus
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-4 focus:outline-none focus:border-amber-400/40"
        />
        <label className="block text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Reason (optional, audited)</label>
        <input
          type="text" value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="reveal-reason-input"
          placeholder="e.g. preparing merchant quote"
          className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[13px] text-white mb-5 focus:outline-none focus:border-amber-400/40"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="reveal-submit-btn"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-1.5">
            {submitting && <Loader2 className="h-3 w-3 animate-spin" />} Reveal
          </button>
        </div>
      </form>
    </div>
  );
}

// Addition C — Required textarea for rate-change reason. Modal popup
// when user attempts to save while rates have changed.
function RateChangeReasonModal({ open, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  const submit = (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Please provide at least 5 characters describing the change.");
      return;
    }
    onConfirm(reason.trim());
    setReason("");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} data-testid="rate-change-reason-modal"
        className="w-full max-w-md mx-4 rounded-2xl border border-white/[0.08] bg-[#0a0a0a] p-6">
        <div className="flex items-center gap-2 mb-2">
          <Pencil className="h-4 w-4 text-cyan-300" />
          <h2 className="text-[14px] font-medium text-white">Why are rates changing?</h2>
        </div>
        <p className="text-[12px] text-zinc-500 mb-4">
          A short justification is required because buy or sell rates are about to change. This appears on every audit row for traceability.
        </p>
        <textarea
          rows={3} value={reason} required
          onChange={(e) => setReason(e.target.value)}
          data-testid="rate-change-reason-input"
          placeholder="e.g. Provider raised buy MDR 0.2pp; passing through to merchant."
          className="w-full rounded-xl border border-white/[0.10] bg-white/[0.03] px-3 py-2 text-[13px] text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-400/40 mb-5"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="rounded-xl px-4 py-2 text-[12.5px] text-zinc-400 hover:text-white">Cancel</button>
          <button type="submit" data-testid="rate-change-reason-submit"
            className="rounded-xl px-4 py-2 text-[12.5px] bg-cyan-400 text-black hover:bg-cyan-300 inline-flex items-center gap-1.5">
            <Save className="h-3.5 w-3.5" /> Save with reason
          </button>
        </div>
      </form>
    </div>
  );
}

function RateHistorySection({ solutionId, revealedFlag }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/solutions/${solutionId}/rate-history`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setRows(data.history || []);
    } catch (_) {
      toast.error("Failed to load rate history");
    } finally {
      setLoading(false);
    }
  }, [solutionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load, revealedFlag]);

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5 mt-5" data-testid="rate-history-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="rate-history-toggle"
        className="flex items-center justify-between w-full"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-zinc-500" />
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">— Rate history</span>
        </div>
        <span className="text-[11.5px] text-zinc-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-4">
          {loading ? (
            <div className="py-6 text-center text-zinc-500"><Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-6 text-center text-zinc-500 text-[12.5px]" data-testid="rate-history-empty">
              No rate changes yet.
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  <th className="text-left py-2 px-2 font-normal">When</th>
                  <th className="text-left py-2 px-2 font-normal">Type</th>
                  <th className="text-left py-2 px-2 font-normal">Field</th>
                  <th className="text-right py-2 px-2 font-normal">Before</th>
                  <th className="text-right py-2 px-2 font-normal">After</th>
                  <th className="text-left py-2 px-2 font-normal">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.history_id} data-testid={`rate-history-row-${h.history_id}`} className="border-b border-white/[0.04]">
                    <td className="py-2 px-2 text-[11px] text-zinc-400 font-mono">{(h.changed_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-2 px-2 text-[11.5px]">
                      <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${
                        h.rate_type === "buy"
                          ? "border-amber-400/30 text-amber-300 bg-amber-400/[0.06]"
                          : "border-emerald-400/30 text-emerald-300 bg-emerald-400/[0.06]"
                      }`}>{h.rate_type}</span>
                    </td>
                    <td className="py-2 px-2 text-[11.5px] text-zinc-300 font-mono">{h.field_path}</td>
                    <td className="py-2 px-2 text-[11.5px] text-zinc-400 text-right font-mono">{h.masked ? "••••" : (h.before ?? "—")}</td>
                    <td className="py-2 px-2 text-[11.5px] text-white text-right font-mono">{h.masked ? "••••" : (h.after ?? "—")}</td>
                    <td className="py-2 px-2 text-[11px] text-zinc-400 max-w-[180px] truncate" title={h.reason || ""} data-testid={`rate-history-reason-${h.history_id}`}>
                      {h.reason || <span className="text-zinc-600">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function UploadDocControl({ solutionId, onUploaded }) {
  const [kind, setKind] = useState("contract");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(
        `${API}/admin/solutions/${solutionId}/documents?kind=${kind}`,
        { method: "POST", credentials: "include", body: fd },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `${r.status}`);
      toast.success(`Uploaded ${data.document?.filename}`);
      onUploaded();
    } catch (err) {
      toast.error(String(err.message || "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={kind} onChange={(e) => setKind(e.target.value)}
        data-testid="solution-doc-kind-select"
        className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-zinc-300 focus:outline-none"
      >
        <option value="contract">Contract</option>
        <option value="rate_sheet">Rate sheet</option>
        <option value="addendum">Addendum</option>
      </select>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="solution-doc-upload-btn"
        className="rounded-lg px-3 py-1.5 text-[12px] bg-white text-black hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
        Upload
      </button>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.heic,.heif"
        onChange={onFile}
      />
    </div>
  );
}

export default function AdminSolutionDetail() {
  const { solutionId } = useParams();
  const [sol, setSol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/solutions/${solutionId}`, { credentials: "include" });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setSol(data);
    } catch (err) {
      toast.error("Failed to load solution");
    } finally {
      setLoading(false);
    }
  }, [solutionId]);

  useEffect(() => { load(); }, [load]);

  const beginEdit = () => {
    setDraft({
      name: sol.name || "",
      notes: sol.notes ?? "",
      sell_rates: { ...(sol.sell_rates || {}) },
      buy_rates: sol.buy_rates ? { ...sol.buy_rates } : null,
      capability_flags: [...(sol.capability_flags || [])],
      three_ds_posture: sol.three_ds_posture || "unknown",
      introducer: sol.introducer || "",
      settlement_fee_pct: sol.settlement_fee_pct ?? 1.0,
      countries: [...(sol.countries || [])],
      region: sol.region || "WW",
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft(null); };

  // Detect if any rate field changed vs the current solution.
  const rateChanged = useMemo(() => {
    if (!editing || !draft) return false;
    const cmp = (a, b) => {
      const keys = RATE_FIELDS.map((f) => f.key);
      return keys.some((k) => Number(a?.[k] ?? 0) !== Number(b?.[k] ?? 0));
    };
    const sellChanged = cmp(draft.sell_rates, sol?.sell_rates);
    const buyChanged = !!sol?.buy_rates_revealed && cmp(draft.buy_rates, sol?.buy_rates);
    return sellChanged || buyChanged;
  }, [editing, draft, sol]);

  const performSave = async (reason) => {
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        notes: draft.notes || null,
        sell_rates: draft.sell_rates,
        introducer: draft.introducer?.trim() || null,
        three_ds_posture: draft.three_ds_posture,
        settlement_fee_pct: Number(draft.settlement_fee_pct ?? 1.0),
        capability_flags: draft.capability_flags,
        countries: draft.countries,
        region: draft.region,
      };
      if (sol.buy_rates_revealed && draft.buy_rates) {
        body.buy_rates = draft.buy_rates;
      }
      if (rateChanged && reason) {
        body.rate_change_reason = reason;
      }
      const r = await fetch(`${API}/admin/solutions/${solutionId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = Array.isArray(data?.detail)
          ? data.detail.map((d) => d.msg || d).join("; ")
          : data?.detail || `${r.status}`;
        throw new Error(msg);
      }
      setSol(data);
      setEditing(false);
      setDraft(null);
      toast.success("Saved");
    } catch (err) {
      toast.error(String(err.message));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = () => {
    if (rateChanged) {
      setReasonOpen(true);
    } else {
      performSave(null);
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archive ${sol.name}? It will be hidden from active lists.`)) return;
    try {
      const r = await fetch(`${API}/admin/solutions/${solutionId}/archive`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setSol(data);
      toast.success("Archived");
    } catch (err) {
      toast.error("Archive failed");
    }
  };

  const reactivate = async () => {
    try {
      const r = await fetch(`${API}/admin/solutions/${solutionId}/reactivate`, {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setSol(data);
      toast.success("Reactivated");
    } catch (err) {
      toast.error("Reactivate failed");
    }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const r = await fetch(
        `${API}/admin/solutions/${solutionId}/documents/${docId}/download`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document? It will be soft-deleted but retained for audit.")) return;
    try {
      const r = await fetch(
        `${API}/admin/solutions/${solutionId}/documents/${docId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!r.ok) throw new Error(`${r.status}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-500" data-testid="solution-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!sol) {
    return (
      <div className="py-24 text-center text-zinc-500" data-testid="solution-detail-not-found">
        Solution not found.
        <div className="mt-3"><Link to="/admin/solutions" className="text-[#00E5FF] text-[13px]">← Back to solutions</Link></div>
      </div>
    );
  }

  const liveDocs = (sol.documents || []).filter((d) => !d.deleted_at);
  const isArchived = sol.status === "archived";

  return (
    <div data-testid="admin-solution-detail-page">
      <Link
        to="/admin/solutions"
        className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-500 hover:text-white mb-5"
        data-testid="solution-detail-back-link"
      >
        <ArrowLeft className="h-3 w-3" /> Solutions
      </Link>

      <header className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">— Solution</div>
          {editing ? (
            <input
              value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              data-testid="solution-edit-name"
              className="w-full max-w-xl rounded-xl border border-white/[0.10] bg-white/[0.03] px-3 py-1.5 text-[22px] font-light text-white focus:outline-none focus:border-[#00E5FF]/40"
            />
          ) : (
            <h1 className="text-[22px] font-light text-white" data-testid="solution-detail-name">{sol.name}</h1>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px]">
            <span className="text-zinc-400" data-testid="solution-detail-provider-pill">
              <Building2 className="h-3 w-3 inline mr-1 text-zinc-600" />
              {sol.provider}
            </span>
            <span className="text-zinc-600">·</span>
            <span className="inline-flex items-center gap-1 text-cyan-300 font-mono" data-testid="solution-detail-internal-code">
              <Tag className="h-3 w-3" />
              {sol.internal_code || "—"}
            </span>
            <span className="text-zinc-600">·</span>
            <span className="font-mono text-zinc-300" data-testid="solution-detail-currency">
              {sol.settlement_currency}{sol.mid_currency && sol.mid_currency !== sol.settlement_currency && <span className="text-zinc-600"> / {sol.mid_currency}</span>}
            </span>
            <span className="text-zinc-600">·</span>
            <span className={isArchived ? "text-zinc-500" : "text-emerald-300"} data-testid={`solution-detail-status-${sol.status}`}>{sol.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <>
              <button onClick={cancelEdit} className="rounded-xl px-3 py-2 text-[12.5px] text-zinc-400 hover:text-white inline-flex items-center gap-1.5" data-testid="solution-edit-cancel">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button
                onClick={saveEdit} disabled={saving}
                data-testid="solution-edit-save"
                className="rounded-xl px-3 py-2 text-[12.5px] bg-white text-black hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </>
          ) : (
            <>
              {!isArchived && (
                <button
                  onClick={beginEdit}
                  data-testid="solution-edit-btn"
                  className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-zinc-300 hover:text-white inline-flex items-center gap-1.5"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              )}
              {isArchived ? (
                <button
                  onClick={reactivate}
                  data-testid="solution-reactivate-btn"
                  className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-2 text-[12.5px] text-emerald-300 hover:bg-emerald-400/[0.12] inline-flex items-center gap-1.5"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reactivate
                </button>
              ) : (
                <button
                  onClick={archive}
                  data-testid="solution-archive-btn"
                  className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[12.5px] text-zinc-300 hover:text-white inline-flex items-center gap-1.5"
                >
                  <Archive className="h-3.5 w-3.5" /> Archive
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Margin alerts banner (Addition B) */}
      {sol.buy_rates_revealed && (
        <MarginAlertBanner alerts={sol.margin_alerts} />
      )}

      {/* At-a-glance sell card — top of the page, always visible */}
      <div className="mb-5">
        <AtAGlanceSellCard sell={editing ? draft.sell_rates : sol.sell_rates} currency={sol.settlement_currency} />
      </div>

      {/* 2-col layout: unified rates table | (quick-quote + coverage) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <UnifiedRatesTable
          buy={editing ? draft.buy_rates : sol.buy_rates}
          sell={editing ? draft.sell_rates : sol.sell_rates}
          margin={sol.margin}
          revealed={!!sol.buy_rates_revealed}
          currency={sol.settlement_currency}
          onReveal={() => setRevealOpen(true)}
        />
        <div className="space-y-5">
          <QuickQuoteCard sol={sol} />
          <MethodCoverageCard sol={sol} />
        </div>
      </div>

      {/* Provider + Capability flags */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <ProviderStrip sol={sol} />
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="capability-card">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-3 flex items-center gap-1.5">
            <Flag className="h-3 w-3" /> — Capability flags
          </div>
          <CapabilityFlags flags={sol.capability_flags} />
        </div>
      </div>

      {/* Ops + commercial detail */}
      <div className="mb-5">
        <OpsNotesCard sol={sol} />
      </div>

      {/* Editing surfaces for the new fields — only when editing */}
      {editing && (
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.03] p-5 mb-5" data-testid="solution-edit-extras">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-cyan-300 mb-4">— Editable taxonomy + sell rates</div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Introducer</label>
              <input
                value={draft.introducer}
                onChange={(e) => setDraft({ ...draft, introducer: e.target.value })}
                data-testid="solution-edit-introducer"
                className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-cyan-400/40"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">3DS posture</label>
              <select
                value={draft.three_ds_posture}
                onChange={(e) => setDraft({ ...draft, three_ds_posture: e.target.value })}
                data-testid="solution-edit-three-ds"
                className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-cyan-400/40"
              >
                {Object.entries(THREE_DS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Settlement fee %</label>
              <input
                type="number" step="0.01" min="0"
                value={draft.settlement_fee_pct}
                onChange={(e) => setDraft({ ...draft, settlement_fee_pct: e.target.value })}
                data-testid="solution-edit-settlement-fee-pct"
                className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-cyan-400/40"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Region</label>
              <select
                value={draft.region}
                onChange={(e) => setDraft({ ...draft, region: e.target.value })}
                data-testid="solution-edit-region"
                className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-cyan-400/40"
              >
                {Object.entries(REGION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Capability flags</label>
            <div className="flex flex-wrap gap-2" data-testid="solution-edit-capability-flags">
              {Object.entries(CAPABILITY_LABELS).map(([k, v]) => (
                <label key={k} className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.capability_flags.includes(k)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDraft({ ...draft, capability_flags: [...draft.capability_flags, k] });
                      } else {
                        setDraft({ ...draft, capability_flags: draft.capability_flags.filter((f) => f !== k) });
                      }
                    }}
                    data-testid={`solution-edit-cap-${k}`}
                  />
                  {v}
                </label>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Notes</label>
            <textarea
              rows={3} value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              data-testid="solution-edit-notes"
              className="w-full rounded-lg border border-white/[0.10] bg-white/[0.03] px-2.5 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-cyan-400/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.03] p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 mb-2">— Sell rates (editable)</div>
              {RATE_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between py-1">
                  <span className="text-[11.5px] text-zinc-400">{f.label}</span>
                  <input
                    type="number" step={f.kind === "days" ? "1" : "0.01"} min="0"
                    value={draft.sell_rates?.[f.key] ?? 0}
                    onChange={(e) => setDraft({
                      ...draft,
                      sell_rates: { ...draft.sell_rates, [f.key]: Number(e.target.value) },
                    })}
                    data-testid={`solution-edit-sell-${f.key}`}
                    className="w-24 rounded-md border border-white/[0.10] bg-white/[0.03] px-2 py-1 text-[11.5px] text-white text-right font-mono focus:outline-none focus:border-emerald-400/40"
                  />
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.03] p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300 mb-2">— Buy rates {sol.buy_rates_revealed ? "(editable)" : "(masked — reveal first)"}</div>
              {RATE_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between py-1">
                  <span className="text-[11.5px] text-zinc-400">{f.label}</span>
                  {sol.buy_rates_revealed && draft.buy_rates ? (
                    <input
                      type="number" step={f.kind === "days" ? "1" : "0.01"} min="0"
                      value={draft.buy_rates[f.key] ?? 0}
                      onChange={(e) => setDraft({
                        ...draft,
                        buy_rates: { ...draft.buy_rates, [f.key]: Number(e.target.value) },
                      })}
                      data-testid={`solution-edit-buy-${f.key}`}
                      className="w-24 rounded-md border border-white/[0.10] bg-white/[0.03] px-2 py-1 text-[11.5px] text-white text-right font-mono focus:outline-none focus:border-amber-400/40"
                    />
                  ) : (
                    <span className="text-[11.5px] text-zinc-600 select-none font-mono">••••</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {rateChanged && (
            <div className="mt-3 text-[11px] text-cyan-300 inline-flex items-center gap-1.5" data-testid="solution-edit-rate-change-notice">
              <Info className="h-3 w-3" /> Rates have changed — you&apos;ll be asked for a reason on save.
            </div>
          )}
        </div>
      )}

      {/* Documents */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] p-5" data-testid="solution-documents-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-1">— Documents</div>
            <p className="text-[11.5px] text-zinc-600">Contracts, rate sheets and addenda. 15 MB max per file.</p>
          </div>
          <UploadDocControl solutionId={solutionId} onUploaded={load} />
        </div>
        {liveDocs.length === 0 ? (
          <div className="text-[12.5px] text-zinc-500 py-6 text-center border border-dashed border-white/[0.06] rounded-xl" data-testid="solution-documents-empty">
            No documents yet.
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {liveDocs.map((d) => (
              <div key={d.doc_id} className="flex items-center justify-between py-3" data-testid={`solution-doc-row-${d.doc_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-4 w-4 text-zinc-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[12.5px] text-white truncate">{d.filename}</div>
                    <div className="text-[10.5px] text-zinc-500">
                      {DOC_KIND_LABELS[d.kind] || d.kind} · {Math.round((d.size || 0) / 1024)} KB · {(d.uploaded_at || "").slice(0, 10)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => downloadDoc(d.doc_id, d.filename)} data-testid={`solution-doc-download-${d.doc_id}`} className="rounded-lg p-1.5 text-zinc-400 hover:text-white" title="Download">
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => deleteDoc(d.doc_id)} data-testid={`solution-doc-delete-${d.doc_id}`} className="rounded-lg p-1.5 text-zinc-400 hover:text-red-400" title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rate history (Module 3, now with reason column) */}
      <RateHistorySection solutionId={solutionId} revealedFlag={!!sol.buy_rates_revealed} />

      <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} solutionId={solutionId} onRevealed={() => load()} />
      <RateChangeReasonModal
        open={reasonOpen}
        onConfirm={(reason) => { setReasonOpen(false); performSave(reason); }}
        onCancel={() => setReasonOpen(false)}
      />
    </div>
  );
}
